/**
 * AuthContext — autenticación global de la aplicación.
 *
 * Centraliza el estado del usuario autenticado (token, nombre, rol) para que
 * cualquier componente pueda leerlo con `useAuth()` sin tocar localStorage
 * directamente.
 *
 * Uso:
 *   // En el punto de entrada (index.tsx) envolver con <AuthProvider>
 *   // En cualquier componente:
 *   const { user, role_id, isAuthenticated, login, logout } = useAuth();
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Datos del usuario autenticado */
export interface AuthUser {
  ltkn: string;
  user_name: string;
  role_id: number;
}

/** Valor expuesto por el contexto */
export interface AuthContextValue {
  /** Datos del usuario o null si no hay sesión */
  user: AuthUser | null;
  /** true si hay token en localStorage */
  isAuthenticated: boolean;
  /** role_id del usuario (0 si no autenticado) */
  role_id: number;
  /**
   * Inicia sesión: guarda en localStorage y actualiza el estado del contexto.
   * @param userData - Datos del usuario devueltos por la API
   */
  login: (userData: AuthUser) => void;
  /**
   * Cierra sesión: borra localStorage, resetea el estado y redirige a "/".
   */
  logout: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Lee el usuario almacenado en localStorage al iniciar la app. */
function getStoredUser(): AuthUser | null {
  const ltkn = localStorage.getItem('ltkn');
  if (!ltkn) return null;
  return {
    ltkn,
    user_name: localStorage.getItem('user_name') ?? '',
    role_id: Number(localStorage.getItem('role_id') ?? 0),
  };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Proveedor de autenticación. Debe envolver toda la aplicación (ver index.tsx).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser);

  const login = useCallback((userData: AuthUser) => {
    localStorage.setItem('ltkn', userData.ltkn);
    localStorage.setItem('user_name', userData.user_name);
    localStorage.setItem('role_id', String(userData.role_id));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('ltkn');
    localStorage.removeItem('user_name');
    localStorage.removeItem('role_id');
    setUser(null);
    window.location.href = '/';
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        role_id: user?.role_id ?? 0,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Accede al contexto de autenticación.
 * @throws {Error} si se usa fuera de `<AuthProvider>`.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  }
  return ctx;
}
